import { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import type { AnyAction } from '@reduxjs/toolkit';

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8901/ws';

let ws: WebSocket | null = null;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_INTERVAL = 3000; // 3 seconds

/**
 * WebSocket Middleware
 * Manages WebSocket connections for real-time features
 */
export const webSocketMiddleware: Middleware = (storeAPI: MiddlewareAPI) => (next) => (action: AnyAction) => {
  const state: any = storeAPI.getState();

  // ===== CONNECT WEBSOCKET =====
  if (action.type === 'collaboration/connect') {
    const token = state.auth?.token;
    const user = state.auth?.user;

    if (!user?.id || !token) {
      console.warn('⚠️  Cannot connect WebSocket: No user or token');
      return next(action);
    }

    // Close existing connection
    if (ws) {
      ws.close();
      ws = null;
    }

    const wsUrl = `${WS_URL}/collaboration/${user.id}?token=${token}`;
    console.log('🔌 Connecting WebSocket:', wsUrl);

    try {
      ws = new WebSocket(wsUrl);

      // Connection opened
      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        reconnectAttempts = 0;
        
        storeAPI.dispatch({
          type: 'collaboration/wsConnected',
          payload: { connected: true },
        });
      };

      // Listen for messages
      ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket message:', data);
          
          storeAPI.dispatch({
            type: 'collaboration/wsMessage',
            payload: data,
          });
        } catch (error) {
          console.error('❌ Failed to parse WebSocket message:', error);
        }
      };

      // Handle errors
      ws.onerror = (error: Event) => {
        console.error('❌ WebSocket error:', error);
        
        storeAPI.dispatch({
          type: 'collaboration/wsError',
          payload: { error: 'Connection error' },
        });
      };

      // Handle connection close
      ws.onclose = (event: CloseEvent) => {
        console.log('🔌 WebSocket closed:', event.code, event.reason);
        
        storeAPI.dispatch({
          type: 'collaboration/wsDisconnected',
          payload: { connected: false },
        });

        // Auto-reconnect logic
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          const delay = RECONNECT_INTERVAL * Math.pow(2, reconnectAttempts);
          console.log(`🔄 Reconnecting in ${delay}ms... (attempt ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);
          
          reconnectTimeout = setTimeout(() => {
            reconnectAttempts++;
            storeAPI.dispatch({ type: 'collaboration/connect' });
          }, delay);
        } else {
          console.error('❌ Max reconnection attempts reached');
        }
      };
    } catch (error) {
      console.error('❌ Failed to create WebSocket:', error);
    }
  }

  // ===== DISCONNECT WEBSOCKET =====
  if (action.type === 'collaboration/disconnect' || action.type === 'auth/logout') {
    console.log('🔌 Disconnecting WebSocket');
    
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (ws) {
      ws.close();
      ws = null;
    }

    reconnectAttempts = 0;
  }

  // ===== SEND MESSAGE =====
  if (action.type === 'collaboration/sendMessage') {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        const message = JSON.stringify(action.payload);
        ws.send(message);
        console.log('📤 Message sent:', action.payload);
      } catch (error) {
        console.error('❌ Failed to send message:', error);
      }
    } else {
      console.warn('⚠️  WebSocket not connected, cannot send message');
    }
  }

  return next(action);
};
